import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import type { SupplierUser } from '../services/types';
import { getAuthService } from '../services';

interface AuthContextValue {
    user: SupplierUser | null;
    loading: boolean;
    setUser: (user: SupplierUser | null) => void;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<SupplierUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;

        const refresh = () => {
            getAuthService()
                .getCurrentUser()
                .then((current) => {
                    if (active) setUser(current);
                })
                .finally(() => {
                    if (active) setLoading(false);
                });
        };

        // Initial load.
        refresh();

        // React to auth events, notably the OAuth (Midway Hosted UI) redirect
        // completing: `signInWithRedirect` fires `signInWithRedirect` on success
        // and `signInWithRedirect_failure` on error. Re-resolve the user so the
        // SSO round-trip lands the employee in the app.
        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                case 'signInWithRedirect':
                case 'signedIn':
                    refresh();
                    break;
                case 'signedOut':
                    if (active) setUser(null);
                    break;
                default:
                    break;
            }
        });

        return () => {
            active = false;
            unsubscribe();
        };
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            loading,
            setUser,
            signOut: async () => {
                await getAuthService().signOut();
                setUser(null);
            },
        }),
        [user, loading],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return ctx;
}

export default AuthContext;
